/**
 * useActivityDetailsFlow — la fiche d'une activité : chargement, vues, gestes.
 *
 * Le legacy est `student/activity/todo/TodoModal.js` (2 196 l.), chemin PARENT. Les appels sont
 * les siens, dans son ordre :
 *  - `refresh()` → `getActivities` avec `with_logs=1`, `activity_ids=<id>` et `include_archived` ;
 *  - « Archiver » → `getActivity` (le legacy lit l'activité avant de poser la question, pour
 *    refuser si l'élève l'a ouverte) puis `deleteActivitiesTodo` — qui n'efface rien : l'activité
 *    part aux archives ;
 *  - « Restaurer » → `restoreArchivedActivities` ;
 *  - « Redonner » → `activityTodoAsStudent`, parce qu'en mode parent le jeton est celui de
 *    l'enfant (`hasRole(user, "student")` est vrai) et que la route n'a donc pas d'identifiant.
 *
 * Le crochet est autonome : il ne lit pas la liste, il charge l'activité qu'on lui nomme. Le
 * réducteur ci-dessous est pur et exporté — chaque transition se joue dans un test, sans écran.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useWindowDimensions } from "react-native"

import { cleanMediaUrl } from "../../../../data/constants/apiConstants"
import { apiIdle, apiRequest } from "../../../../data/redux/actions/api"
import { activitiesService } from "../../../../data/services/activitiesService"
import { type ActivityDetailsModalProps } from "../../../../design-system/templates/ActivityDetailsModal"
import { type SnackbarProps } from "../../../../design-system/molecules/Snackbar"
import {
    activityDetailsActions,
    activityDisplayModes,
    defaultActivityDisplayMode,
    redoActivityBody,
    toUIActivityDetails,
    type ApiActivityDetails,
} from "../../adapters/parent/Activities/activityDetailsAdapter"
import { type ApiSchoolSubject } from "../../adapters/parent/shared/schoolSubjectAdapter"
import { type UIActivityDetailsAction, type UIActivityDetailsDialog, type UIActivityDisplayMode } from "../types"
import { apiErrorMessage } from "./apiErrorMessage"
import { useApiMutation } from "./useApiMutation"

/** Au-delà de 800 px, la fiche peut montrer l'énoncé et le rendu côte à côte (`canShowSplit`). */
const SPLIT_MIN_WIDTH = 800

const LOAD_FAILED = "Impossible d'ouvrir cette activité. Réessayez dans un instant."
const ARCHIVE_FAILED = "Impossible d'archiver cette activité. Réessayez dans un instant."
const RESTORE_FAILED = "Impossible de restaurer cette activité. Réessayez dans un instant."
const REDO_FAILED = "Impossible de redonner cette activité. Réessayez dans un instant."

/** L'accusé du legacy, sans son ✅ : le bandeau du design system porte déjà son glyphe. */
const REDONE_NOTICE = "L'activité a été redonnée à l'élève"

export type ActivityDetailsFlowState = {
    activityId: string | number | null
    /** Ouverte depuis les archives : « Restaurer » remplace « Archiver ». */
    fromArchived: boolean
    visible: boolean
    /** La vue choisie. `null` → celle que l'activité appelle d'elle-même. */
    mode: UIActivityDisplayMode | null
    dialog: UIActivityDetailsDialog | null
    submitting: UIActivityDetailsAction | null
    error: string | null
}

export const initialActivityDetailsFlowState: ActivityDetailsFlowState = {
    activityId: null,
    fromArchived: false,
    visible: false,
    mode: null,
    dialog: null,
    submitting: null,
    error: null,
}

export type ActivityDetailsFlowAction =
    | { type: "OPEN"; activityId: string | number; fromArchived: boolean }
    | { type: "SELECT_MODE"; mode: UIActivityDisplayMode }
    | { type: "START"; action: UIActivityDetailsAction }
    | { type: "ASK"; dialog: UIActivityDetailsDialog }
    | { type: "CONFIRM" }
    | { type: "CANCEL" }
    | { type: "DONE" }
    | { type: "FAILED"; message: string }
    | { type: "CLOSE" }

export function activityDetailsFlowReducer(
    state: ActivityDetailsFlowState,
    action: ActivityDetailsFlowAction
): ActivityDetailsFlowState {
    switch (action.type) {
        case "OPEN":
            return {
                ...initialActivityDetailsFlowState,
                activityId: action.activityId,
                fromArchived: action.fromArchived,
                visible: true,
            }

        case "SELECT_MODE":
            return { ...state, mode: action.mode }

        case "START":
            return { ...state, submitting: action.action, error: null }

        // Le legacy ferme la fiche avant d'ouvrir la boîte (`handleShowModal()` puis `DialogM.show`).
        // Ici elle reste montée mais effacée — c'est `<ActivityDetailsModal hidden>` qui s'en charge.
        case "ASK":
            return { ...state, dialog: action.dialog, submitting: null }

        case "CONFIRM":
            return state.dialog == null ? state : { ...state, dialog: null, submitting: "archive", error: null }

        case "CANCEL":
            return { ...state, dialog: null, submitting: null }

        case "DONE":
            return initialActivityDetailsFlowState

        case "FAILED":
            return { ...state, dialog: null, submitting: null, error: action.message }

        case "CLOSE":
            return initialActivityDetailsFlowState

        default:
            return state
    }
}

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: unknown
    routeParams?: { id?: string | number }
    urlParams?: { activity_ids?: string | number }
}

export type UseActivityDetailsFlowArgs = {
    /** L'enfant — son identifiant part dans l'URL d'archivage et de restauration. */
    studentId?: string | number | null
    /** Rejoué après un geste : la liste doit montrer le nouvel état. */
    onChanged?: () => void
    /** Place à réserver sous l'accusé — la hauteur de la barre d'onglets. */
    snackbarBottomOffset?: number
}

export type UseActivityDetailsFlowResult = {
    /** Ouvre la fiche d'une activité. Depuis les archives, le second argument le dit. */
    open: (activityId: string | number, options?: { fromArchived?: boolean }) => void
    /** Les props de `<ActivityDetailsModal>`, prêtes à être étalées. */
    details: ActivityDetailsModalProps
    /** L'accusé « c'est fait » — un seul geste en produit un, « Redonner ». */
    notice: SnackbarProps
}

export function useActivityDetailsFlow(args: UseActivityDetailsFlowArgs = {}): UseActivityDetailsFlowResult {
    const { studentId, onChanged, snackbarBottomOffset = 0 } = args
    const dispatch = useDispatch()
    const { width } = useWindowDimensions()
    const wide = width > SPLIT_MIN_WIDTH

    const [state, dispatchUi] = useReducer(activityDetailsFlowReducer, initialActivityDetailsFlowState)
    const [raw, setRaw] = useState<ApiActivityDetails | null>(null)
    const [noticeMessage, setNoticeMessage] = useState<string | null>(null)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subjects = useSelector((s: any) => s?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined)
    const activityTemplates = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => s?.data?.staticData?.activity_templates as { id?: string | number }[] | undefined
    )

    // ── Lecture (patron B) : la réponse de `getActivities` ne crée aucune entrée dans
    // `state.data` (le réducteur ne fait que rafraîchir les tableaux existants), donc la fiche
    // garde sa charge utile. La tranche est partagée avec la liste et le mode live : le drapeau
    // `awaiting` et le renvoi de `activity_ids` disent si la réponse est la nôtre.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loadReq = useSelector((s: any) => s?.api?.getActivities as ApiSlice | undefined)
    const awaitingLoadRef = useRef(false)
    const handledLoadRef = useRef(false)
    const pendingActivityIdRef = useRef<string | number | null>(null)

    const load = useCallback(
        (activityId: string | number, fromArchived: boolean) => {
            awaitingLoadRef.current = true
            handledLoadRef.current = false
            pendingActivityIdRef.current = activityId
            dispatch(
                apiRequest(
                    activitiesService.getActivities,
                    {},
                    {},
                    { with_logs: 1, activity_ids: activityId, include_archived: fromArchived ? 1 : 0 }
                ) as never
            )
        },
        [dispatch]
    )

    useEffect(() => {
        if (!awaitingLoadRef.current || !loadReq?.success || handledLoadRef.current) return
        const answered = loadReq.urlParams?.activity_ids
        const asked = pendingActivityIdRef.current
        if (asked != null && answered != null && String(answered) !== String(asked)) return
        handledLoadRef.current = true
        awaitingLoadRef.current = false
        const rows = (Array.isArray(loadReq.data) ? loadReq.data : []) as ApiActivityDetails[]
        setRaw(rows.find((row) => String(row?.id) === String(asked)) ?? rows[0] ?? null)
    }, [loadReq?.success, loadReq?.data, loadReq?.urlParams?.activity_ids])

    useEffect(() => {
        if (!awaitingLoadRef.current || !loadReq?.error) return
        awaitingLoadRef.current = false
        dispatchUi({ type: "FAILED", message: apiErrorMessage(loadReq.error, LOAD_FAILED) })
        dispatch(apiIdle(activitiesService.getActivities) as never)
    }, [dispatch, loadReq?.error])

    // ── « Archiver » : le legacy relit l'activité avant de poser la question, pour refuser quand
    // l'élève l'a ouverte. En mode parent le jeton EST celui de l'élève, donc le refus ne se
    // déclenche pas — l'appel reste, il est ce que fait la production aujourd'hui.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkReq = useSelector((s: any) => s?.api?.getActivity as ApiSlice | undefined)
    const awaitingCheckRef = useRef(false)
    const handledCheckErrorRef = useRef(false)
    const pendingCheckIdRef = useRef<string | number | null>(null)

    useEffect(() => {
        if (!awaitingCheckRef.current || !checkReq?.success) return
        const checked = checkReq.routeParams?.id
        if (
            pendingCheckIdRef.current != null &&
            checked != null &&
            String(checked) !== String(pendingCheckIdRef.current)
        ) {
            return
        }
        awaitingCheckRef.current = false
        dispatchUi({ type: "ASK", dialog: "archive" })
        dispatch(apiIdle(activitiesService.getActivity) as never)
    }, [dispatch, checkReq?.success, checkReq?.routeParams?.id])

    useEffect(() => {
        if (!awaitingCheckRef.current || !checkReq?.error || handledCheckErrorRef.current) return
        handledCheckErrorRef.current = true
        awaitingCheckRef.current = false
        dispatchUi({ type: "FAILED", message: apiErrorMessage(checkReq.error, ARCHIVE_FAILED) })
        dispatch(apiIdle(activitiesService.getActivity) as never)
    }, [dispatch, checkReq?.error])

    useEffect(
        () => () => {
            dispatch(apiIdle(activitiesService.getActivity) as never)
            dispatch(apiIdle(activitiesService.getActivities) as never)
        },
        [dispatch]
    )

    const finish = useCallback(
        (message?: string) => {
            setRaw(null)
            setNoticeMessage(message ?? null)
            dispatchUi({ type: "DONE" })
            onChanged?.()
        },
        [onChanged]
    )

    /**
     * Pas de `scopeParam` sur ces trois écritures : la fiche ne porte qu'une ligne, l'activité
     * ouverte, et leurs corps (`activity_ids`, `material_id`) ne sont pas renvoyés par la tranche
     * en cas d'échec — il n'y aurait rien à comparer.
     */
    const archive = useApiMutation({
        service: activitiesService.deleteActivitiesTodo,
        fallback: ARCHIVE_FAILED,
        onSuccess: () => finish(),
        onError: (message) => dispatchUi({ type: "FAILED", message }),
    }).start

    const restore = useApiMutation({
        service: activitiesService.restoreArchivedActivities,
        fallback: RESTORE_FAILED,
        onSuccess: () => finish(),
        onError: (message) => dispatchUi({ type: "FAILED", message }),
    }).start

    const redo = useApiMutation({
        service: activitiesService.activityTodoAsStudent,
        fallback: REDO_FAILED,
        onSuccess: () => finish(REDONE_NOTICE),
        onError: (message) => dispatchUi({ type: "FAILED", message }),
    }).start

    const open = useCallback(
        (activityId: string | number, options?: { fromArchived?: boolean }) => {
            const fromArchived = options?.fromArchived === true
            setRaw(null)
            dispatchUi({ type: "OPEN", activityId, fromArchived })
            load(activityId, fromArchived)
        },
        [load]
    )

    const close = useCallback(() => {
        awaitingLoadRef.current = false
        awaitingCheckRef.current = false
        setRaw(null)
        dispatchUi({ type: "CLOSE" })
    }, [])

    const onAction = useCallback(
        (action: UIActivityDetailsAction) => {
            if (state.activityId == null) return
            if (action === "archive") {
                dispatchUi({ type: "START", action })
                awaitingCheckRef.current = true
                handledCheckErrorRef.current = false
                pendingCheckIdRef.current = state.activityId
                dispatch(apiRequest(activitiesService.getActivity, { id: state.activityId }) as never)
                return
            }
            dispatchUi({ type: "START", action })
            if (action === "restore") {
                restore({ student: studentId }, { activity_ids: [state.activityId] })
                return
            }
            const templateId = activityTemplates?.find(
                (template) => String(template?.id) === String(raw?.activity_template_id)
            )?.id
            redo(
                {},
                redoActivityBody(raw, {
                    activityTemplateId: templateId,
                    schoolSubjectId: raw?.school_subject_id,
                })
            )
        },
        [activityTemplates, dispatch, raw, redo, restore, state.activityId, studentId]
    )

    const onConfirmDialog = useCallback(() => {
        if (state.activityId == null) return
        dispatchUi({ type: "CONFIRM" })
        archive({ student: studentId }, { activity_ids: [state.activityId] })
    }, [archive, state.activityId, studentId])

    const activity = useMemo(() => {
        const subject = (subjects ?? []).find((s) => String(s?.id) === String(raw?.school_subject_id)) ?? null
        return toUIActivityDetails(raw, { subject, cleanUrl: cleanMediaUrl })
    }, [raw, subjects])

    const modes = useMemo(() => activityDisplayModes(raw, wide), [raw, wide])

    return {
        open,
        details: {
            visible: state.visible,
            onClose: close,
            activity,
            // La fiche s'ouvre dès le geste ; tant que l'activité n'est pas lue (et que la lecture
            // n'a pas échoué), elle en montre la forme.
            loading: state.visible && raw == null && state.error == null,
            modes,
            mode: state.mode ?? defaultActivityDisplayMode(raw, wide),
            onModeChange: (mode) => dispatchUi({ type: "SELECT_MODE", mode }),
            actions: activityDetailsActions(raw, state.fromArchived),
            onAction,
            submitting: state.submitting,
            dialog: state.dialog,
            onConfirmDialog,
            onCancelDialog: () => dispatchUi({ type: "CANCEL" }),
            error: state.error,
        },
        notice: {
            visible: noticeMessage != null,
            message: noticeMessage ?? "",
            onDismiss: () => setNoticeMessage(null),
            bottomOffset: snackbarBottomOffset,
        },
    }
}
